/* eslint-disable no-param-reassign */
import { type UnknownAction } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';

import type { FormBuilderState } from './types/builderTypes';

import { resetBuilder } from './actions/resetAction';
import { builderUiActions, builderUiReducer } from './slices/builderUiSlice';
import {
  fieldBackupsActions,
  fieldBackupsReducer,
} from './slices/fieldBackupsSlice';
import {
  fieldErrorsActions,
  fieldErrorsReducer,
} from './slices/fieldErrorsSlice';
import { formErrorsActions, formErrorsReducer } from './slices/formErrorsSlice';

export * from './selectors/builderUiSelectors';
export * from './selectors/fieldBackupsSelectors';
export * from './selectors/fieldErrorsSelectors';
export * from './selectors/formErrorsSelectors';
export * from './types/builderTypes';

/* =========================================
   Combined reducer with reset handling
========================================= */

const combinedBuilderReducer = combineReducers({
  ui: builderUiReducer,
  errors: formErrorsReducer,
  fieldErrors: fieldErrorsReducer,
  fieldBackups: fieldBackupsReducer,
});

export const builderReducer = (
  state: FormBuilderState | undefined,
  action: UnknownAction
): FormBuilderState => {
  if (resetBuilder.match(action)) {
    return combinedBuilderReducer(undefined, action) as FormBuilderState;
  }

  return combinedBuilderReducer(state, action) as FormBuilderState;
};

export default builderReducer;

/* =========================================
   Unified actions export
========================================= */

// builderUiSlice is typed as Slice<BuilderUiState> for TS7056 (lib declaration emit),
// so builderUiActions is generic. Cast to a typed map so the export type does not leak `any`.
type BuilderUiActionCreator = (...args: unknown[]) => {
  type: string;
  payload?: unknown;
};
type BuilderUiActionsMap = Record<string, BuilderUiActionCreator>;

export const builderActions = {
  ...formErrorsActions,
  ...fieldErrorsActions,
  ...fieldBackupsActions,
  ...(builderUiActions as BuilderUiActionsMap),
  resetBuilder,
} as typeof formErrorsActions &
  typeof fieldErrorsActions &
  typeof fieldBackupsActions &
  BuilderUiActionsMap & { resetBuilder: typeof resetBuilder };
